import { useQuery } from '@apollo/client/react';
import { Badge, Box, BoxProps, ButtonProps, Center, Flex, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { GroupRequest } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/navigation';
import React from 'react';
type RequestGridCardProps = {
  groupRequestId: string;
  requestGridCardProps?: BoxProps;
  requestGridCardButtonProps?: ButtonProps;
};
export const RequestGridCard: React.FC<RequestGridCardProps> = props => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const router = useRouter();
  const query = useQuery(gql`
      query ($groupRequestId: String) {
        groupRequest(groupRequestId: $groupRequestId) {
          id
          groupRequestId
          canUserEdit
          displayTitle
          title
          generatedTitle
          bodyIsGenerated
          requestingUserId
          requestingUser {
            id
            userId
            displayName
            firstName
            lastName
            pictureUrl
            title
            ${UserAvatar.fragments.ScoUser()}
          }
          groupRequestType {
            id
            title
            colorHexCode
          }
          publishStatusKey
          publishStatus {
            id
            key
            title
          }
        }
      }
    `, {
    variables: {
      groupRequestId: props.groupRequestId
    }
  });
  const groupRequest: GroupRequest = query.data?.groupRequest;
  if (!groupRequest) {
    return <Center padding={4} width={'full'}>
        <Spinner />
      </Center>;
  }
  const requestProfileHref = groupRequest.canUserEdit ? `/requests/manage/${groupRequest.groupRequestId}` : `/requests/${groupRequest.groupRequestId}`;
  return <Flex align={'stretch'} bgColor={'bg'} border={'1px solid'} borderColor={`${groupRequest.groupRequestType?.colorHexCode}50`} borderRadius={'md'} boxShadow={'xs'} cursor={'pointer'} justify={'stretch'} transition={'all 100ms ease-in-out'} _hover={{
    borderColor: groupRequest.groupRequestType?.colorHexCode || 'colorPalette.border',
    transform: 'scale(1.025)',
    boxShadow: 'sm'
  }} onClick={() => {
    router.push(requestProfileHref);
  }} {...props.requestGridCardProps}>
      <Box borderRadius={'md'} flex={1} padding={getBreakpointValue([4, 4, 6])} bg={{
      _dark: `linear-gradient(to bottom, ${groupRequest.groupRequestType?.colorHexCode}60 25%, ${groupRequest.groupRequestType?.colorHexCode}40 100%)`,
      _light: `linear-gradient(to bottom, ${groupRequest.groupRequestType?.colorHexCode}20 25%, ${groupRequest.groupRequestType?.colorHexCode}20 100%)`
    }}>
        <VStack align={'stretch'} gap={6}>
          <HStack>
            <Badge bgColor={groupRequest.groupRequestType?.colorHexCode} boxShadow={'xs'} color={'white'} variant={'label'}>
              {groupRequest.groupRequestType?.title || 'Request'}
            </Badge>
          </HStack>
          <Heading lineHeight={'short'} size={getBreakpointValue(['md', 'lg', 'lg', 'xl'])}>
            {groupRequest.displayTitle}
          </Heading>
          {groupRequest.description && <Text>{groupRequest.description}</Text>}
          <HStack gap={4}>
            <UserAvatar avatarProps={{
            size: 'md'
          }} user={groupRequest.requestingUser} />
            <VStack align={'stretch'} flex={1} gap={0.5}>
              <Heading fontSize={'md'} lineHeight={1}>
                {groupRequest.requestingUser.displayName}
              </Heading>
              <Text fontSize={'sm'} opacity={0.5}>
                {groupRequest.requestingUser.company?.name}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </Box>
    </Flex>;
};